import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Save,
  RefreshCw,
  Check,
  X,
  Search,
  Users,
  AlertCircle,
  FileSpreadsheet,
  Info,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { buildApiUrl } from '../../utils/apiSetup';

const EmployeePayrollMasterEditor = ({ onClose }) => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [editingCell, setEditingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [changedCells, setChangedCells] = useState(new Set());
  const [globalAllowance, setGlobalAllowance] = useState({
    da_percentage: "",
    hra_percentage: "",
    washing_allowance: "",
    family_planning_allowance: "",
    conveyance_allowance: "",
  });
  const [bulkApplying, setBulkApplying] = useState(false);
  const tableRef = useRef(null);
  const inputRef = useRef(null);

  const [scales, setScales] = useState([]);
  const [selectedScope, setSelectedScope] = useState("all");
  const [activeEmployeeCount, setActiveEmployeeCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Field definitions for Employee Payroll Master (Simplified structure)
  const payrollMasterFields = {
    employee_info: {
      title: "Employee Information",
      fields: [
        { key: "emp_code", label: "Emp Code", width: 120, readonly: true },
        { key: "emp_name", label: "Employee Name", width: 200, readonly: true },
        { key: "designation", label: "Designation", width: 150, readonly: true },
      ],
    },
    earnings: {
      title: "Earnings - Master Values",
      color: "bg-green-50 border-green-200",
      fields: [
        {
          key: "basic",
          label: "Basic (₹)",
          width: 120,
          type: "currency",
          editable: true,
          required: true,
        },
        {
          key: "da_percentage",
          label: "DA %",
          width: 100,
          type: "percentage",
          editable: true,
        },
        {
          key: "hra_percentage",
          label: "HRA %",
          width: 100,
          type: "percentage",
          editable: true,
        },
        {
          key: "cca",
          label: "CCA (₹)",
          width: 100,
          type: "currency",
          editable: true,
        },
      ],
    },
    allowances: {
      title: "Allowances - Master Values (₹)",
      color: "bg-purple-50 border-purple-200",
      fields: [
        {
          key: "washing_allowance",
          label: "Washing",
          width: 120,
          type: "currency",
          editable: true,
        },
        {
          key: "family_planning_allowance",
          label: "Family Planning",
          width: 120,
          type: "currency",
          editable: true,
        },
        {
          key: "conveyance_allowance",
          label: "Conveyance",
          width: 120,
          type: "currency",
          editable: true,
        },
      ],
    },
  };

  // Fetch all employee masters
  const fetchEmployeeMasters = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl("/api/v1/benfish-payroll/employee-master"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (!response.ok) {
        let detail = "Failed to fetch employee masters";
        try {
          const err = await response.json();
          if (typeof err.detail === "string") detail = err.detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }

      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];

      const formattedData = items.map((item) => ({
        id: item.employee.id,
        emp_code: item.employee.emp_code,
        emp_name: item.employee.employee_name || item.employee.emp_name || "",
        sec_code: item.employee.sec_code,
        scale: item.employee.scale,
        pf_code: item.employee.pf_code,
        designation: item.employee.designation || item.employee.emp_desig || "",
        master_id: item.master?.id || null,
        basic: item.master?.basic || 0,
        da_percentage: item.master?.da_percentage || 18.0,
        hra_percentage: item.master?.hra_percentage || 12.0,
        cca: item.master?.cca || 0,
        washing_allowance: item.master?.washing_allowance || 0,
        family_planning_allowance: item.master?.family_planning_allowance || 0,
        conveyance_allowance: item.master?.conveyance_allowance || 0,
        washing_allowance_editable: item.master?.washing_allowance_editable ?? true,
        family_planning_allowance_editable: item.master?.family_planning_allowance_editable ?? true,
        conveyance_allowance_editable: item.master?.conveyance_allowance_editable ?? true,
        is_active: item.master?.is_active ?? true,
        effective_from: item.master?.effective_from || null,
        notes: item.master?.notes || null,
      }));

      setEmployees(formattedData);
      setError("");
    } catch (err) {
      console.error("Error fetching employee masters:", err);
      setError(err.message || "Failed to load employee payroll masters");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all active scales
  const fetchScales = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/v1/designations/scales"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setScales(data);
      }
    } catch (err) {
      console.error("Error fetching scales:", err);
    }
  };

  // Fetch count of active employees in selected scope
  const fetchActiveCount = async (scope, scaleId) => {
    try {
      setLoadingCount(true);
      const url = `/api/v1/benfish-payroll/employee-master/active-count?scope_type=${scope}${scaleId ? `&scale_id=${scaleId}` : ""}`;
      const response = await fetch(buildApiUrl(url), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setActiveEmployeeCount(data.count);
      }
    } catch (err) {
      console.error("Error fetching active count:", err);
    } finally {
      setLoadingCount(false);
    }
  };

  useEffect(() => {
    fetchEmployeeMasters();
    fetchScales();
    fetchActiveCount("all", null);
  }, []);

  const handleScopeChange = (e) => {
    const value = e.target.value;
    setSelectedScope(value);
    if (value === "all") {
      fetchActiveCount("all", null);
    } else {
      fetchActiveCount("scale", value);
    }
  };

  // Auto-focus input when editing
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Check if cell is editable based on backend-derived eligibility flags
  const isCellEditable = (employee, fieldDef) => {
    if (!fieldDef.editable || fieldDef.readonly) return false;
    if (fieldDef.key === "washing_allowance") return !!employee.washing_allowance_editable;
    if (fieldDef.key === "family_planning_allowance") return !!employee.family_planning_allowance_editable;
    if (fieldDef.key === "conveyance_allowance") return !!employee.conveyance_allowance_editable;
    return true;
  };

  // Handle cell click
  const handleCellClick = (employeeId, fieldKey, fieldDef) => {
    const employee = employees.find((emp) => emp.id === employeeId);
    if (employee && isCellEditable(employee, fieldDef)) {
      setEditingCell({ employeeId, fieldKey });
    }
  };

  // Handle cell value change
  const handleCellChange = (employeeId, fieldKey, value) => {
    setEmployees((prev) => {
      const updated = prev.map((emp) => {
        if (emp.id === employeeId) {
          const updatedEmp = { ...emp, [fieldKey]: value };
          updatedEmp.gross_pay = calculateGrossPay(updatedEmp);
          return updatedEmp;
        }
        return emp;
      });
      return updated;
    });

    setChangedCells((prev) => new Set([...prev, `${employeeId}-${fieldKey}`]));
  };

  // Calculate gross pay
  const calculateGrossPay = (employee) => {
    const basic = parseFloat(employee.basic) || 0;
    const da = basic * ((parseFloat(employee.da_percentage) || 0) / 100);
    const hra = basic * ((parseFloat(employee.hra_percentage) || 0) / 100);
    const cca = parseFloat(employee.cca) || 0;
    const washing = parseFloat(employee.washing_allowance) || 0;
    const fp = parseFloat(employee.family_planning_allowance) || 0;
    const conv = parseFloat(employee.conveyance_allowance) || 0;
    return basic + da + hra + cca + washing + fp + conv;
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleKeyDown = (e, employeeId, fieldKey) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      setEditingCell(null);
    } else if (e.key === "Escape") {
      setEditingCell(null);
    }
  };

  // Open confirmation modal for bulk-applying payroll values
  const handlePreApplyBulk = () => {
    const da = globalAllowance.da_percentage === "" ? null : parseFloat(globalAllowance.da_percentage);
    const hra = globalAllowance.hra_percentage === "" ? null : parseFloat(globalAllowance.hra_percentage);
    const washing = globalAllowance.washing_allowance === "" ? null : parseFloat(globalAllowance.washing_allowance);
    const fp = globalAllowance.family_planning_allowance === "" ? null : parseFloat(globalAllowance.family_planning_allowance);
    const conv = globalAllowance.conveyance_allowance === "" ? null : parseFloat(globalAllowance.conveyance_allowance);

    if (da == null && hra == null && washing == null && fp == null && conv == null) {
      setError("Enter at least one value to apply");
      return;
    }

    setShowConfirmModal(true);
  };

  // Bulk-apply payroll values (partial updates supported) after confirmation
  const handleApplyBulkConfirm = async () => {
    setShowConfirmModal(false);
    
    const da = globalAllowance.da_percentage === "" ? null : parseFloat(globalAllowance.da_percentage);
    const hra = globalAllowance.hra_percentage === "" ? null : parseFloat(globalAllowance.hra_percentage);
    const washing = globalAllowance.washing_allowance === "" ? null : parseFloat(globalAllowance.washing_allowance);
    const fp = globalAllowance.family_planning_allowance === "" ? null : parseFloat(globalAllowance.family_planning_allowance);
    const conv = globalAllowance.conveyance_allowance === "" ? null : parseFloat(globalAllowance.conveyance_allowance);

    setBulkApplying(true);
    setError("");
    setSuccess("");

    try {
      const isScale = selectedScope !== "all";
      const payload = {
        scope_type: isScale ? "scale" : "all",
        scale_id: isScale ? parseInt(selectedScope) : null,
        da_percentage: da,
        hra_percentage: hra,
        washing_allowance: washing,
        family_planning_allowance: fp,
        conveyance_allowance: conv,
      };

      const res = await fetch(buildApiUrl("/api/v1/benfish-payroll/employee-master/global-payroll-values"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to update global values");
      }

      const data = await res.json();
      setSuccess(data.message || `Successfully applied defaults to ${data.employees_affected} employee(s).`);

      setGlobalAllowance({
        da_percentage: "",
        hra_percentage: "",
        washing_allowance: "",
        family_planning_allowance: "",
        conveyance_allowance: "",
      });

      // Refresh employee list and the active employee count
      fetchEmployeeMasters();
      fetchActiveCount(payload.scope_type, payload.scale_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkApplying(false);
    }
  };

  const getButtonText = () => {
    if (bulkApplying) return "Applying...";
    if (selectedScope === "all") return "Apply to All Employees";
    const selectedScale = scales.find((s) => s.id === parseInt(selectedScope));
    return `Apply to ${selectedScale ? selectedScale.code : "Selected Scale"} Employees`;
  };

  // Save all inline changes
  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError("");

      const changedEmployeeIds = new Set(
        Array.from(changedCells).map((cell) => cell.split("-")[0]),
      );

      const updatePromises = Array.from(changedEmployeeIds).map(
        async (employeeId) => {
          const employee = employees.find(
            (emp) => emp.id === parseInt(employeeId),
          );
          if (!employee) return;

          const masterData = {
            employee_id: employee.id,
            basic: parseFloat(employee.basic) || 0,
            cca: parseFloat(employee.cca) || 0,
            washing_allowance: parseFloat(employee.washing_allowance) || 0,
            family_planning_allowance: parseFloat(employee.family_planning_allowance) || 0,
            conveyance_allowance: parseFloat(employee.conveyance_allowance) || 0,
            da_percentage: parseFloat(employee.da_percentage) || 18.0,
            hra_percentage: parseFloat(employee.hra_percentage) || 12.0,
            is_active: true,
          };

          return fetch(buildApiUrl("/api/v1/benfish-payroll/employee-master"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
            body: JSON.stringify(masterData),
          }).then((response) => {
            if (!response.ok) {
              return response.json().then((data) => {
                throw new Error(
                  data.detail || "Failed to save employee master",
                );
              });
            }
            return response.json();
          });
        },
      );

      await Promise.all(updatePromises);

      setSuccess(
        `Successfully saved changes for ${changedEmployeeIds.size} employee(s).`
      );
      setChangedCells(new Set());

      await fetchEmployeeMasters();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving employee masters:", err);
      setError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    return parseFloat(value).toFixed(2);
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.emp_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const renderCell = (employee, fieldDef) => {
    const value = employee[fieldDef.key];
    const isEditing =
      editingCell?.employeeId === employee.id &&
      editingCell?.fieldKey === fieldDef.key;
    const hasChanged = changedCells.has(`${employee.id}-${fieldDef.key}`);

    const isEditable = isCellEditable(employee, fieldDef);

    // Render non-eligible placeholder cell with tooltip
    if (!isEditable && ["washing_allowance", "family_planning_allowance", "conveyance_allowance"].includes(fieldDef.key)) {
      return (
        <div
          className="px-3 py-2 text-sm text-gray-400 bg-gray-100 cursor-not-allowed text-right font-mono"
          title="Employee is not eligible for this allowance."
        >
          ₹0.00
        </div>
      );
    }

    if (isEditing && isEditable) {
      return (
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          className="w-full h-full px-2 py-1 text-sm border-2 border-blue-500 rounded focus:outline-none"
          value={value || ""}
          onChange={(e) =>
            handleCellChange(employee.id, fieldDef.key, e.target.value)
          }
          onBlur={handleCellBlur}
          onKeyDown={(e) => handleKeyDown(e, employee.id, fieldDef.key)}
        />
      );
    }

    return (
      <div
        className={`px-3 py-2 text-sm cursor-${isEditable ? "text" : "default"} ${
          fieldDef.type === "currency" ? "text-right font-mono" : ""
        } ${hasChanged ? "bg-yellow-100 font-semibold" : ""} ${
          isEditable ? "hover:bg-blue-50" : ""
        }`}
        onClick={() => handleCellClick(employee.id, fieldDef.key, fieldDef)}
      >
        {fieldDef.type === "currency"
          ? formatCurrency(value)
          : value || "0"}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg">
          Loading employee payroll masters...
        </span>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="w-7 h-7 text-blue-600" />
              Employee Payroll Master
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Define default payroll values for each employee. These values will
              be used when creating new payroll runs.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <span className="text-sm text-gray-600">
              {filteredEmployees.length} employee
              {filteredEmployees.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {changedCells.size > 0 && (
              <span className="text-sm text-amber-600 font-medium">
                {changedCells.size} unsaved change
                {changedCells.size !== 1 ? "s" : ""}
              </span>
            )}
            <button
              onClick={fetchEmployeeMasters}
              disabled={loading}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || changedCells.size === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save All Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <strong>Note:</strong> Monthly payroll edits will not affect these
            master values. Future payroll runs will always use the values
            defined here.
          </div>
        </div>

        {/* Global Allowance Update Section */}
        <div className="mt-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-purple-700" />
            <h3 className="text-sm font-semibold text-purple-900">
              Global Payroll Default Values
            </h3>
          </div>
          <p className="text-xs text-purple-700 mb-3">
            Update default DA, HRA, or Allowance values for active employee payroll
            masters. Only filled fields will be applied; empty values will be ignored.
          </p>

          <div className="mb-4 max-w-xs">
            <label className="block text-xs font-semibold text-purple-900 mb-1">
              Apply To
            </label>
            <div className="relative">
              <select
                value={selectedScope}
                onChange={handleScopeChange}
                className="w-full px-3 py-2 border border-purple-300 bg-white rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none appearance-none cursor-pointer text-gray-700 font-medium"
              >
                <option value="all">All Employees</option>
                {scales.map((scale) => (
                  <option key={scale.id} value={scale.id}>
                    {scale.code} {scale.name ? `- ${scale.name}` : ""}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-purple-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-1 font-medium">
              {loadingCount ? (
                "Loading employee count..."
              ) : selectedScope === "all" ? (
                `This will update payroll defaults for all ${activeEmployeeCount} active employees.`
              ) : (
                `This will update payroll defaults for ${activeEmployeeCount} active employees assigned to ${
                  scales.find((s) => s.id === parseInt(selectedScope))?.code || ""
                }.`
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                DA Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g. 18"
                value={globalAllowance.da_percentage}
                onChange={(e) =>
                  setGlobalAllowance((prev) => ({
                    ...prev,
                    da_percentage: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                HRA Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g. 12"
                value={globalAllowance.hra_percentage}
                onChange={(e) =>
                  setGlobalAllowance((prev) => ({
                    ...prev,
                    hra_percentage: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Washing Allowance (₹)
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 500"
                value={globalAllowance.washing_allowance}
                onChange={(e) =>
                  setGlobalAllowance((prev) => ({
                    ...prev,
                    washing_allowance: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Family Planning (₹)
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 750"
                value={globalAllowance.family_planning_allowance}
                onChange={(e) =>
                  setGlobalAllowance((prev) => ({
                    ...prev,
                    family_planning_allowance: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Conveyance (₹)
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 1000"
                value={globalAllowance.conveyance_allowance}
                onChange={(e) =>
                  setGlobalAllowance((prev) => ({
                    ...prev,
                    conveyance_allowance: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handlePreApplyBulk}
              disabled={
                bulkApplying ||
                (globalAllowance.da_percentage === "" &&
                  globalAllowance.hra_percentage === "" &&
                  globalAllowance.washing_allowance === "" &&
                  globalAllowance.family_planning_allowance === "" &&
                  globalAllowance.conveyance_allowance === "")
              }
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {bulkApplying ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4" />
                  {getButtonText()}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div ref={tableRef} className="flex-1 overflow-auto">
        <table className="w-full border-collapse bg-white">
          <thead className="sticky top-0 z-10">
            {/* Section Headers */}
            <tr className="bg-gray-100 border-b-2 border-gray-300">
              <th className="border border-gray-300 p-2 text-xs font-semibold text-gray-700 sticky left-0 bg-gray-100 z-20">
                #
              </th>
              {Object.entries(payrollMasterFields).map(
                ([sectionKey, section]) => (
                  <th
                    key={sectionKey}
                    colSpan={section.fields.length}
                    className={`border border-gray-300 p-2 text-sm font-bold ${section.color || ""}`}
                  >
                    {section.title}
                  </th>
                ),
              )}
            </tr>

            {/* Column Headers */}
            <tr className="bg-gray-50 border-b border-gray-300">
              <th className="border border-gray-300 p-2 text-xs font-semibold text-gray-600 w-12 sticky left-0 bg-gray-50 z-20">
                Row
              </th>
              {Object.values(payrollMasterFields).map((section) =>
                section.fields.map((field) => (
                  <th
                    key={field.key}
                    className="border border-gray-300 p-2 text-xs font-semibold text-gray-600 whitespace-nowrap"
                    style={{ width: field.width }}
                  >
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </th>
                )),
              )}
            </tr>
          </thead>

          <tbody>
            {filteredEmployees.map((employee, index) => (
              <tr
                key={employee.id}
                className="hover:bg-gray-50 border-b border-gray-200"
              >
                <td className="border border-gray-300 p-2 text-xs text-center font-medium text-gray-600 sticky left-0 bg-white z-10">
                  {index + 1}
                </td>
                {Object.values(payrollMasterFields).map((section) =>
                  section.fields.map((field) => (
                    <td
                      key={`${employee.id}-${field.key}`}
                      className={`border border-gray-300 ${section.color || ""}`}
                    >
                      {renderCell(employee, field)}
                    </td>
                  )),
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No employees found</p>
          </div>
        )}
      </div>
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-sm"
            onClick={() => setShowConfirmModal(false)}
          />
          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl border border-gray-100 p-6 max-w-md w-full mx-4 z-10"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 text-purple-700 rounded-full">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Confirm Bulk Update
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  {selectedScope === "all"
                    ? `You are about to update payroll defaults for all ${activeEmployeeCount} active employees. Continue?`
                    : `You are about to update payroll defaults for ${activeEmployeeCount} active employees in scale ${
                        scales.find((s) => s.id === parseInt(selectedScope))?.code || ""
                      }. Continue?`}
                </p>
                
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mt-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                    Values to apply:
                  </span>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-medium text-gray-700">
                    {globalAllowance.da_percentage && <div>DA: {globalAllowance.da_percentage}%</div>}
                    {globalAllowance.hra_percentage && <div>HRA: {globalAllowance.hra_percentage}%</div>}
                    {globalAllowance.washing_allowance && <div>Washing: ₹{globalAllowance.washing_allowance}</div>}
                    {globalAllowance.family_planning_allowance && <div>Family Planning: ₹{globalAllowance.family_planning_allowance}</div>}
                    {globalAllowance.conveyance_allowance && <div>Conveyance: ₹{globalAllowance.conveyance_allowance}</div>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyBulkConfirm}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Confirm
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EmployeePayrollMasterEditor;
